/**
 * The Venue Analytics line chart: a hand-rolled inline SVG (there is no
 * charting dependency in the project) with one series per venue × metric.
 *
 * Hover (or touch) anywhere on the plot snaps a crosshair to the nearest
 * night. The tooltip then compares every visible venue that week: each
 * venue's closest night within HOVER_WINDOW_DAYS, with its raw values and
 * its rolling average at that point.
 */
import { useMemo, useRef, useState } from 'react';
import { Box, Flex, Text } from '@radix-ui/themes';
import {
  AttendancePoint,
  MetricKind,
  ROLLING_WINDOW,
  VenueRef,
  colorForVenue,
  formatDate,
  formatDayDate,
  formatValue,
  groupByVenue,
  metricLabel,
  monthTicks,
  niceStep,
  regressionLine,
  rollingAligned,
  valueFor,
} from './attendanceMath';

export type Overlay = 'raw' | 'rolling' | 'trend';

// Venues usually play on different weekdays, so "the same week" is the
// comparison unit for the tooltip.
const HOVER_WINDOW_DAYS = 4;
const DAY_MS = 24 * 60 * 60 * 1000;

// Chart geometry (SVG user units; the SVG scales to its container width).
const WIDTH = 760;
const HEIGHT = 320;
const PAD = { top: 16, right: 16, bottom: 32, left: 40 };
const PLOT_W = WIDTH - PAD.left - PAD.right;
const PLOT_H = HEIGHT - PAD.top - PAD.bottom;

interface ChartSeries {
  key: string;
  venueId: number;
  venueName: string;
  color: string;
  dashed: boolean;
  kind: MetricKind;
  points: AttendancePoint[];
  rolling: (number | null)[];
}

interface Props {
  points: AttendancePoint[];
  venues: VenueRef[];
  kinds: MetricKind[];
  overlays: Set<Overlay>;
}

export default function AttendanceChart({ points, venues, kinds, overlays }: Props): JSX.Element {
  const svgRef = useRef<SVGSVGElement>(null);
  const [hoverT, setHoverT] = useState<number | null>(null);

  const showRaw = overlays.has('raw');
  const showRolling = overlays.has('rolling');
  const showTrend = overlays.has('trend');
  // When any overlay sits on top of the raw series, dim the raw line/dots so
  // the rolling-avg / trend lines read as the focus.
  const rawDimmed = showRaw && (showRolling || showTrend);

  const series = useMemo<ChartSeries[]>(() => {
    const out: ChartSeries[] = [];
    for (const [venueId, pts] of groupByVenue(points).entries()) {
      for (const kind of kinds) {
        out.push({
          key: `${venueId}-${kind}`,
          venueId,
          venueName: pts[0]?.venueName ?? 'Unknown',
          color: colorForVenue(venueId, venues),
          // In "both" mode, draw the people line dashed so the two lines of
          // one venue stay distinguishable.
          dashed: kinds.length > 1 && kind === 'people',
          kind,
          points: pts,
          rolling: rollingAligned(pts, kind, ROLLING_WINDOW),
        });
      }
    }
    return out;
  }, [points, venues, kinds]);

  const trends = useMemo(
    () => new Map(series.map((s) => [s.key, regressionLine(s.points, s.kind)])),
    [series]
  );

  // Shared scales across every visible series.
  const times = points.map((p) => new Date(p.date).getTime());
  const minT = Math.min(...times);
  const maxT = Math.max(...times);
  const allValues: number[] = [];
  for (const s of series) {
    if (showRaw) {
      s.points.forEach((p) => allValues.push(valueFor(p, s.kind)));
    }
    if (showRolling) {
      s.rolling.forEach((v) => v !== null && allValues.push(v));
    }
    const tr = trends.get(s.key);
    if (showTrend && tr) {
      allValues.push(tr[0].value, tr[1].value);
    }
  }
  const dataMax = Math.max(1, ...allValues);
  // Avg/team is fractional. Every other metric gets whole-number gridlines.
  const wholeNumbers = !kinds.includes('avg');
  const step = wholeNumbers ? Math.max(1, Math.ceil(niceStep(dataMax, 4))) : niceStep(dataMax, 4);
  const maxValue = Math.ceil(dataMax / step) * step;
  const yTicks: number[] = [];
  for (let v = 0; v <= maxValue + 1e-9; v += step) {
    yTicks.push(v);
  }
  const xTicks = monthTicks(minT, maxT, 8);

  function x(t: number): number {
    if (maxT === minT) {
      return PAD.left + PLOT_W / 2;
    }
    return PAD.left + ((t - minT) / (maxT - minT)) * PLOT_W;
  }
  function y(value: number): number {
    return PAD.top + PLOT_H - (value / maxValue) * PLOT_H;
  }

  function onPointerMove(e: React.PointerEvent<SVGSVGElement>): void {
    const svg = svgRef.current;
    if (!svg || times.length === 0) {
      return;
    }
    const rect = svg.getBoundingClientRect();
    const sx = ((e.clientX - rect.left) / rect.width) * WIDTH;
    let best = times[0] ?? 0;
    for (const t of times) {
      if (Math.abs(x(t) - sx) < Math.abs(x(best) - sx)) {
        best = t;
      }
    }
    setHoverT(best);
  }

  // For each visible venue, its night closest to the hovered time.
  const hoverRows = useMemo(() => {
    if (hoverT === null) {
      return [];
    }
    const rows: { venueId: number; venueName: string; color: string; index: number; point: AttendancePoint }[] = [];
    const seen = new Set<number>();
    for (const s of series) {
      if (seen.has(s.venueId)) {
        continue;
      }
      seen.add(s.venueId);
      let bestIdx = -1;
      let bestD = Infinity;
      s.points.forEach((p, i) => {
        const d = Math.abs(new Date(p.date).getTime() - hoverT);
        if (d < bestD) {
          bestD = d;
          bestIdx = i;
        }
      });
      const point = s.points[bestIdx];
      if (point && bestD <= HOVER_WINDOW_DAYS * DAY_MS) {
        rows.push({ venueId: s.venueId, venueName: s.venueName, color: s.color, index: bestIdx, point });
      }
    }
    return rows;
  }, [hoverT, series]);

  const hoverX = hoverT === null ? null : x(hoverT);
  // Flip the tooltip to the left of the crosshair on the right side of the plot.
  const tooltipOnLeft = hoverX !== null && hoverX > WIDTH * 0.55;

  return (
    <Box className="attendance-graph__chart">
      <div className="attendance-graph__plot">
        <svg
          ref={svgRef}
          viewBox={`0 0 ${WIDTH} ${HEIGHT}`}
          role="img"
          aria-label="Attendance over time by venue"
          width="100%"
          onPointerMove={onPointerMove}
          onPointerLeave={() => setHoverT(null)}
        >
          {yTicks.map((tick) => (
            <g key={`yt-${tick}`}>
              <line x1={PAD.left} x2={WIDTH - PAD.right} y1={y(tick)} y2={y(tick)} className="attendance-graph__gridline" />
              <text x={PAD.left - 8} y={y(tick)} className="attendance-graph__axis-label" textAnchor="end" dominantBaseline="middle">
                {wholeNumbers ? tick : tick.toFixed(1)}
              </text>
            </g>
          ))}

          {xTicks.map((t) => (
            <g key={`xt-${t}`}>
              <line x1={x(t)} x2={x(t)} y1={PAD.top + PLOT_H} y2={PAD.top + PLOT_H + 4} className="attendance-graph__axis" />
              <text x={x(t)} y={PAD.top + PLOT_H + 18} className="attendance-graph__axis-label" textAnchor="middle">
                {new Date(t).toLocaleDateString(undefined, { month: 'short', year: '2-digit' })}
              </text>
            </g>
          ))}

          <line
            x1={PAD.left}
            x2={WIDTH - PAD.right}
            y1={PAD.top + PLOT_H}
            y2={PAD.top + PLOT_H}
            className="attendance-graph__axis"
          />

          {series.map((s) => {
            const coords = s.points.map((p) => ({
              px: x(new Date(p.date).getTime()),
              py: y(valueFor(p, s.kind)),
              id: p.eventId,
            }));
            const rollingPath = s.points
              .map((p, i) => {
                const v = s.rolling[i];
                return v === null || v === undefined ? null : `${x(new Date(p.date).getTime())},${y(v)}`;
              })
              .filter(Boolean)
              .join(' ');
            const trend = trends.get(s.key);
            return (
              <g key={s.key}>
                {showRaw && (
                  <g className={rawDimmed ? 'attendance-graph__raw--dim' : undefined}>
                    {coords.length > 1 && (
                      <polyline
                        points={coords.map((c) => `${c.px},${c.py}`).join(' ')}
                        fill="none"
                        stroke={s.color}
                        strokeWidth={2}
                        strokeLinejoin="round"
                        strokeLinecap="round"
                        strokeDasharray={s.dashed ? '5 4' : undefined}
                      />
                    )}
                    {coords.map((c) => (
                      <circle key={c.id} cx={c.px} cy={c.py} r={rawDimmed ? 2.5 : 3.5} fill={s.color} />
                    ))}
                  </g>
                )}
                {showRolling && rollingPath.includes(' ') && (
                  <polyline
                    points={rollingPath}
                    fill="none"
                    stroke={s.color}
                    strokeWidth={3.5}
                    strokeOpacity={0.8}
                    strokeLinejoin="round"
                    strokeLinecap="round"
                    strokeDasharray={s.dashed ? '8 5' : undefined}
                  />
                )}
                {showTrend && trend && (
                  <line
                    x1={x(trend[0].t)}
                    y1={y(trend[0].value)}
                    x2={x(trend[1].t)}
                    y2={y(trend[1].value)}
                    stroke={s.color}
                    strokeWidth={1.5}
                    strokeDasharray="2 4"
                    strokeLinecap="round"
                  />
                )}
              </g>
            );
          })}

          {/* hover layer: crosshair + full-opacity rings on the compared nights */}
          {hoverX !== null && (
            <g pointerEvents="none">
              <line x1={hoverX} x2={hoverX} y1={PAD.top} y2={PAD.top + PLOT_H} className="attendance-graph__crosshair" />
              {hoverRows.flatMap((r) =>
                series
                  .filter((s) => s.venueId === r.venueId)
                  .map((s) => (
                    <circle
                      key={`hl-${s.key}`}
                      cx={x(new Date(r.point.date).getTime())}
                      cy={y(showRaw ? valueFor(r.point, s.kind) : (s.rolling[r.index] ?? valueFor(r.point, s.kind)))}
                      r={5.5}
                      fill={s.color}
                      className="attendance-graph__hover-dot"
                    />
                  ))
              )}
            </g>
          )}

          {/* transparent hit area so the pointer is tracked across the whole plot */}
          <rect x={PAD.left} y={PAD.top} width={PLOT_W} height={PLOT_H} fill="transparent" />
        </svg>

        {hoverX !== null && hoverT !== null && hoverRows.length > 0 && (
          <div
            className="attendance-graph__tooltip"
            style={{
              left: `${(hoverX / WIDTH) * 100}%`,
              transform: tooltipOnLeft ? 'translateX(calc(-100% - 12px))' : 'translateX(12px)',
            }}
          >
            <Text as="div" size="1" color="gray" mb="2">
              Week of {formatDate(hoverT)}
            </Text>
            <Flex direction="column" gap="2">
              {hoverRows.map((r) => {
                const venueSeries = series.filter((s) => s.venueId === r.venueId);
                const rollingParts = venueSeries
                  .map((s) => {
                    const v = s.rolling[r.index];
                    return v === null || v === undefined ? null : `${formatValue(v, s.kind)} ${metricLabel(s.kind)}`;
                  })
                  .filter(Boolean);
                return (
                  <Box key={r.venueId}>
                    <Flex align="center" gap="2">
                      <span className="attendance-graph__swatch" style={{ background: r.color }} />
                      <Text size="2" weight="bold">
                        {r.venueName}
                      </Text>
                    </Flex>
                    <Text as="div" size="1" color="gray">
                      {formatDayDate(r.point.date)} · {r.point.eventLabel}
                    </Text>
                    <Text as="div" size="2">
                      <strong>{r.point.teamCount}</strong> teams · <strong>{r.point.peopleCount}</strong> people ·{' '}
                      <strong>{formatValue(valueFor(r.point, 'avg'), 'avg')}</strong> per team
                    </Text>
                    {rollingParts.length > 0 && (
                      <Text as="div" size="1" color="gray">
                        {ROLLING_WINDOW}-night avg: {rollingParts.join(' · ')}
                      </Text>
                    )}
                  </Box>
                );
              })}
            </Flex>
          </div>
        )}
      </div>

      {/* legend: venue colours, then what each line style means */}
      <Flex gap="4" wrap="wrap" mt="3" align="center">
        {series.map((s) => (
          <Flex key={s.key} align="center" gap="2">
            <span
              className="attendance-graph__swatch"
              style={{
                background: s.dashed
                  ? `repeating-linear-gradient(90deg, ${s.color} 0 5px, transparent 5px 9px)`
                  : s.color,
              }}
            />
            <Text size="2">{kinds.length > 1 ? `${s.venueName} (${metricLabel(s.kind)})` : s.venueName}</Text>
          </Flex>
        ))}
        {showRaw && (
          <Flex align="center" gap="2">
            <span className="attendance-graph__line-key attendance-graph__line-key--raw" />
            <Text size="1" color="gray">Each night</Text>
          </Flex>
        )}
        {showRolling && (
          <Flex align="center" gap="2">
            <span className="attendance-graph__line-key attendance-graph__line-key--rolling" />
            <Text size="1" color="gray">{ROLLING_WINDOW}-night avg</Text>
          </Flex>
        )}
        {showTrend && (
          <Flex align="center" gap="2">
            <span className="attendance-graph__line-key attendance-graph__line-key--trend" />
            <Text size="1" color="gray">Trend</Text>
          </Flex>
        )}
      </Flex>
    </Box>
  );
}
